import { Grid } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalLoader } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MoreHoriz } from '@mui/icons-material';
import { dateFormat } from '@nx-smartmonkey/shared/helpers';
import CreatedAtIcon from '../../../../../assets/img/backoffice/created_at.svg';
import StopIcon from '../../../../../assets/img/backoffice/stop.svg';
import ClientIcon from '../../../../../assets/img/backoffice/user.svg';
import VehicleIcon from '../../../../../assets/img/backoffice/vehicle.svg';
import * as routing from '../../../../../constants/Routing';
import { UserBase } from '../../../../../domain/backoffice/user/UserBase';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveProject } from '../../../../../hooks/backoffice/projects/useRetrieveProject';
import Text from '../../../../../react-components/Text';
import { DataTable } from '../../../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../../../services/requests';
import { InfoBox, InfoBoxItem, InfoBoxText } from '../../../shared/InfoBox';
import { SystemsInfoBox } from '../../../shared/SystemsInfoBox';
import { getUsersTableColumns } from '../../../shared/detailsUsersTableColumns';
import { BackofficeClientsProjectActionsMenu } from '../shared/BackofficeClientsProjectActionsMenu';

export const BackofficeClientsProjectsDetails = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { data: project, isLoading, error, run: refetchProject } = useRetrieveProject({ projectId });

  useEffect(() => {
    if (error) {
      navigate(routing.BACKOFFICE.CLIENTS.PROJECTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading) {
    return (
      <Grid
        item
        container
        direction="column"
        style={{ height: `100%`, width: `100%` }}
        alignItems="center"
        justifyContent="center"
      >
        <RoutalLoader />
      </Grid>
    );
  }

  if (!project) return null;

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <Grid
        style={{ padding: `16px 22px`, borderBottom: `1px solid #e0e0e0` }}
        container
        item
        justifyContent="space-between"
        direction="row"
        wrap="nowrap"
      >
        <Grid item container direction="row" alignItems="center">
          <RoutalAvatar large url={project.avatar_url} getUploadAsBase64={getUploadAsBase64} label={project.label} />

          <HSpacer medium />

          <Text variant="body-large">{project.label},</Text>

          <HSpacer small />

          <Text
            variant="button"
            onClick={(e: any) => {
              navigate(`${routing.BACKOFFICE.CLIENTS.ORGANIZATIONS}/details/${project.organization.id}`);
            }}
          >
            {project.organization.label}
          </Text>
        </Grid>

        <Grid item container direction="row" alignItems="center" justifyContent="flex-end">
          <BackofficeClientsProjectActionsMenu
            project={project}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
            }}
            refreshAfterUpdate={() => refetchProject()}
          />
        </Grid>
      </Grid>

      <div style={{ padding: `16px 22px`, overflow: `auto` }}>
        <Grid container direction="column" spacing={2}>
          <Grid item style={{ maxWidth: `500px` }}>
            <SystemsInfoBox
              email={project.users.find((u) => u.id === project.organization.owner_id)?.email}
              subscription={project.organization.subscription}
            />
          </Grid>

          <Grid item style={{ maxWidth: `500px` }}>
            <InfoBox title={intl.formatMessage({ id: `backoffice.information` })}>
              <div style={{ height: `calc(100% - 32px)`, width: `calc(100% - 16px)`, padding: `0 8px` }}>
                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.projects.max_vehicles` })}
                  icon={<img src={VehicleIcon} style={{ width: `14px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{project.resources.max_vehicles_plan ?? `-`}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.projects.max_clients` })}
                  icon={<img src={ClientIcon} style={{ width: `14px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{project.resources.max_clients ?? `-`}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.projects.max_services_plan` })}
                  icon={<img src={StopIcon} style={{ width: `14px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{project.resources.max_stops_plan ?? `-`}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.projects.created_at` })}
                  icon={<img src={CreatedAtIcon} style={{ width: `14px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{dateFormat(project.created_at)}</InfoBoxText>
                </InfoBoxItem>
              </div>
            </InfoBox>
          </Grid>

          <Grid item style={{ maxWidth: `1500px` }}>
            <InfoBox title={intl.formatMessage({ id: `backoffice.clients.users_num` }, { num: project.users.length })}>
              <DataTable
                loading={isLoading}
                showCheckboxes={false}
                docs={project.users.map((user: UserBase) => ({ doc: user }))}
                noFooter
                hover
                total={project.users.length}
                columns={getUsersTableColumns({
                  navigate: (destinationUrl: string) => navigate(destinationUrl),
                })}
              />
            </InfoBox>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};
